import React, { useEffect, useState } from "react";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import CardsView from "./supabase-data/CardsView";
import { LoadingSpinner, ErrorDisplay, NoDataDisplay } from "./supabase-data/LoadingAndError";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { getUserLocation } from '@/utils/userSettingsUtils';
import { useToast } from "@/components/ui/use-toast";
import { FontSizeOption } from '@/types/display';

// Helper function to recursively sanitize data and remove debug attributes
const sanitizeData = (data: any): any => {
  if (!data) return data;
  
  if (typeof data === 'string') {
    return data.replace(/data-lov-id="[^"]*"|data-lov-name="[^"]*"|data-component-path="[^"]*"|data-component-line="[^"]*"|data-component-file="[^"]*"|data-component-name="[^"]*"|data-component-content="[^"]*"/g, '');
  }
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }
  
  if (typeof data === 'object') {
    const result: Record<string, any> = {};
    for (const key in data) {
      result[key] = sanitizeData(data[key]);
    }
    return result;
  }
  
  return data;
};

interface SupabaseDataContent {
  url: string;
  refreshInterval?: number; // in minutes
  showDescriptions?: boolean;
  fontSize?: FontSizeOption;
  selectedSections?: string[];
  displayOptions?: {
    showTitle: boolean;
    showDescription: boolean;
    showItems: boolean;
    showColumns: boolean;
  };
}

interface SupabaseDataPageProps {
  content: SupabaseDataContent;
  isHomePage?: boolean;
}

const SupabaseDataPage: React.FC<SupabaseDataPageProps> = ({ content, isHomePage }) => {
  const isMobile = useIsMobile();
  const [location, setLocation] = useState<{ lat: string | number; lng: string | number; tzid?: string } | null>(null);
  const { toast } = useToast();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const loc = await getUserLocation();
      setLocation(loc);
    })();
  }, []);

  // Only fetch data when location is loaded (for zmanim pages)
  const shouldWaitForLocation = content.url && content.url.includes('zmanim');
  const locationLoaded = !shouldWaitForLocation || (location && location.lat && location.lng);

  const {
    data: fetchedData,
    error: fetchError,
    isLoading: isFetching,
    refetch
  } = useSupabaseData({
    url: content.url || "https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/daily-study",
    refreshInterval: content.refreshInterval,
    location: locationLoaded ? location : undefined
  });

  useEffect(() => {
    if (fetchError) {
      setError(fetchError);
      toast({
        title: "שגיאה בטעינת הנתונים",
        description: fetchError,
        variant: "destructive"
      });
    } else if (fetchedData) {
      setData(fetchedData);
      setError(null);
    }
    setIsLoading(isFetching);
  }, [fetchedData, fetchError, isFetching, toast]);

  // Set up auto-refresh if refreshInterval is provided
  useEffect(() => {
    if (!content.refreshInterval) return;

    const interval = setInterval(() => {
      refetch();
    }, content.refreshInterval * 60 * 1000); // Convert minutes to milliseconds

    return () => clearInterval(interval);
  }, [content.refreshInterval, refetch]);

  // Default display options
  const displayOptions = content.displayOptions || {
    showTitle: true,
    showDescription: true,
    showItems: true,
    showColumns: true
  };

  // Log the current state and environment for debugging
  React.useEffect(() => {
    console.log("SupabaseDataPage rendered with URL:", content.url);
    console.log("Current data:", data);
    console.log("Error state:", error);
    console.log("Loading state:", isLoading);
    console.log("Is mobile:", isMobile);
    console.log("Display options:", displayOptions);
    console.log("Complete content configuration:", {
      ...content,
      displayFormat: "cards" // Always use cards view
    });
    console.log("User location:", location);
  }, [content.url, data, error, isLoading, isMobile, displayOptions, content, location]);

  if (shouldWaitForLocation && (!location || !location.lat || !location.lng)) {
    return (
      <div className={isMobile ? 'max-w-full px-4' : 'max-w-4xl mx-auto p-6'}>
        <Alert className="mb-6">
          <AlertTitle>לא הוגדר מיקום</AlertTitle>
          <AlertDescription>
            <p className="mb-2">יש להגדיר מיקום בלוח הבקרה כדי להציג את זמני היום.</p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner isLoading={isLoading} />;
  }

  if (error) {
    // Special handling for CORS errors
    if (error.includes('CORS')) {
      return (
        <div className={`${isMobile ? 'max-w-full px-4' : 'max-w-4xl'} mx-auto p-6`}>
          <Alert className="mb-6">
            <AlertTitle>שגיאת CORS בגישה לנתונים</AlertTitle>
            <AlertDescription>
              <p className="mb-2">
                לא ניתן לגשת לנתונים בגלל מגבלות אבטחה בדפדפן (CORS). 
                יש לעדכן את הגדרות פונקציית Edge ב-Supabase כדי לאפשר גישה מהדומיין הנוכחי.
              </p>
              <p className="mb-4 text-sm">
                הוסיפו את ה-header: <code>Access-Control-Allow-Origin: *</code> (או הגדירו את הדומיין הספציפי שלכם)
                בפונקציית ה-Edge.
              </p>
              <Button size="sm" variant="outline" onClick={refetch} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                <span>נסה שוב</span>
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      );
    }
    
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  if (!data) {
    return <NoDataDisplay />;
  }

  // Ensure data has items array to avoid type errors
  const safeData = {
    ...data,
    items: Array.isArray(data.items) ? sanitizeData(data.items) : []
  };

  return (
    <div className="space-y-4">
      {displayOptions.showTitle && safeData.title && (
        <h2 className={`text-2xl font-bold text-center ${content.fontSize ? `text-${content.fontSize}` : ''}`}>
          {safeData.title}
        </h2>
      )}
      {displayOptions.showDescription && safeData.description && (
        <p className={`text-gray-600 text-center ${content.fontSize ? `text-${content.fontSize}` : ''}`}>
          {safeData.description}
        </p>
      )}
      {displayOptions.showItems && (
        <CardsView 
          data={safeData} 
          onRetry={refetch} 
          isHomePage={isHomePage} 
          showDescriptions={content.showDescriptions}
          showColumns={displayOptions.showColumns}
          selectedSections={content.selectedSections}
          fontSize={content.fontSize}
          displayOptions={displayOptions}
        />
      )}
    </div>
  );
};

export default SupabaseDataPage;
